import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import ProjectTabs, { useWorkSummary } from "../components/work/ProjectTabs";
import QuickFilters, { useProjectFilters } from "../components/work/QuickFilters";
import { TypeIcon, PriorityIcon, Avatar, StatusPill, fmtAgo } from "../components/work/workMeta";
import { fmtH, fmtMoney } from "../components/work/WorkLog";
import { BoardFilters, applyFilters, filterCtx, useWorkItems } from "./Board";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";

const LIST_PARAMS = {};

export default function WorkItems() {
  const { projectId } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { summary, sprints, reload } = useWorkSummary(projectId);
  const [filters, setFilters] = useProjectFilters(projectId);
  const [sort, setSort] = useState({ by: "updated_at", dir: -1 });
  const { items, load, queryError } = useWorkItems(projectId, filters, LIST_PARAMS);
  if (!summary || !items) return <Skeleton className="h-96 w-full" />;
  const order = { HIGHEST: 0, HIGH: 1, MEDIUM: 2, LOW: 3, LOWEST: 4 };
  const rows = applyFilters(items, filters, filterCtx(summary, user)).sort((a, b) => {
    const va = sort.by === "priority" ? order[a.priority] : sort.by === "key" ? a.number : (a[sort.by] || "");
    const vb = sort.by === "priority" ? order[b.priority] : sort.by === "key" ? b.number : (b[sort.by] || "");
    return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
  });
  const th = (label, by) => (
    <TableHead className="cursor-pointer select-none whitespace-nowrap capitalize" onClick={() => setSort({ by, dir: sort.by === by ? -sort.dir : 1 })} data-testid={`sort-${by}`}>
      {label}{sort.by === by ? (sort.dir > 0 ? " ↑" : " ↓") : ""}
    </TableHead>
  );
  return (
    <div className="space-y-4" data-testid="work-items-page">
      <ProjectTabs summary={summary} sprints={sprints} onCreated={() => { reload(); load(); }} />
      <QuickFilters projectId={projectId} summary={summary} filters={filters} setFilters={setFilters} />
      <BoardFilters summary={summary} filters={filters} setFilters={setFilters} queryError={queryError} count={items.length} />
      <div className="apha-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead></TableHead>{th(t("key"), "key")}<TableHead>{t("title")}</TableHead>{th(t("status"), "status_name")}{th(t("priority"), "priority")}
            <TableHead>{t("assignee")}</TableHead><TableHead>{t("points")}</TableHead>{th(`${t("loggedShort")} / ${t("estimateShort")}`, "logged_hours")}{th(t("remainingHours"), "remaining_hours")}
            {summary.can_see_price && th(t("price"), "price")}{th(t("dueDate"), "due_date")}{th(t("updated"), "updated_at")}
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((i) => (
              <TableRow key={i.id} className="cursor-pointer" data-testid={`work-row-${i.key}`} onClick={() => navigate(`/work/${i.key}`)}>
                <TableCell><TypeIcon type={i.type} /></TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{i.key}</TableCell>
                <TableCell className="max-w-[420px]"><div className="truncate">{i.title}</div>{i.labels?.length > 0 && <div className="flex gap-1 mt-0.5">{i.labels.map((l) => <span key={l} className="text-[10px] bg-secondary rounded px-1.5 text-muted-foreground">{l}</span>)}</div>}</TableCell>
                <TableCell><StatusPill name={i.status_name} category={i.status_category} />{i.is_blocked && <span className="ml-1 text-[10px] text-[#991B1B] font-semibold">BLOCKED</span>}</TableCell>
                <TableCell><span className="inline-flex items-center gap-1 text-xs"><PriorityIcon priority={i.priority} />{t(i.priority)}</span></TableCell>
                <TableCell><span className="inline-flex items-center gap-1.5 text-xs"><Avatar name={i.assignee_name} />{i.assignee_name || "—"}</span></TableCell>
                <TableCell className="text-xs">{i.story_points ?? "—"}</TableCell>
                <TableCell className="text-xs whitespace-nowrap" data-testid={`work-hours-${i.key}`}>{fmtH(i.logged_hours || 0)} / {fmtH(i.estimate_hours)}</TableCell>
                <TableCell className="text-xs">{i.remaining_hours ?? "—"}</TableCell>
                {summary.can_see_price && <TableCell className="text-xs whitespace-nowrap" data-testid={`work-price-${i.key}`}>{i.price != null ? fmtMoney(i.price, summary.project.currency) : "—"}</TableCell>}
                <TableCell className={`text-xs whitespace-nowrap ${i.due_date && i.status_category !== "DONE" && i.due_date < new Date().toISOString().slice(0, 10) ? "text-[#991B1B] font-medium" : ""}`}>{i.due_date || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtAgo(i.updated_at)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={12} className="text-center text-sm text-muted-foreground py-8" data-testid="work-items-empty">{t("noWorkItems")}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
